import { CreditBookmark, CreditType } from 'types/credit';
import { FunctionComponent, ReactElement, ReactNode, useEffect } from 'react';
import CreditDetails from 'pages/CreditDetails';
import Error404 from 'pages/Error404';
import Help from 'pages/Help';
import Home from 'pages/Home';
import SavedCredits from 'pages/SavedCredits';
import { useCourseSearchStore } from 'store/course-search';
import useCreditBookmarks from 'hooks/use-credit-bookmarks';
import useSelectedSourceInstitution from 'hooks/use-selected-source-institution';

// assume context is default for this file

const mockCreditBookmarks: CreditBookmark[] = [
  {
    creditItemID: '2a9e0b04-3740-4cf6-963d-cfe20817ae99',
    creditType: CreditType.PRECEDENT,
    timestamp: 1691411429238,
  },
  {
    creditItemID: '7f941c7e-350a-4784-8a82-3fcb1c8a3a7b',
    creditType: CreditType.ARTICULATION,
    timestamp: 1691411429238,
  },
  {
    creditItemID: 'a81629d2-2024-491e-98b7-908425ffc411',
    creditType: CreditType.ARTICULATION,
    timestamp: 1691411429238,
  },
  {
    creditItemID: '94e199a3-6118-4069-8bba-27fb4a0747ee',
    creditType: CreditType.ARTICULATION,
    timestamp: 1691411429238,
  },
  {
    creditItemID: 'd62b0d11-6f56-4836-ab94-e57b946bc50b',
    creditType: CreditType.PRECEDENT,
    timestamp: 1691411433152,
  },
  {
    creditItemID: 'bb25dd30-1157-4c2d-9811-788610b25be5',
    creditType: CreditType.ARTICULATION,
    timestamp: 1691411433152,
  },
];

// allows us to write actions before rendering a page

const MonsidoWrapperComponent: FunctionComponent<{
  children: ReactElement;
  searchTerm?: string;
  selectedInstitution?: string;
  creditBookmarks?: CreditBookmark[];
}> = ({ children, searchTerm, selectedInstitution, creditBookmarks }) => {
  const { setSelectedSourceInstitution } = useSelectedSourceInstitution();
  const { setSearchTerm } = useCourseSearchStore();
  const { setCreditBookmarks } = useCreditBookmarks();
  useEffect(() => {
    if (searchTerm) {
      setSearchTerm(searchTerm);
    }
    if (selectedInstitution) {
      setSelectedSourceInstitution(selectedInstitution);
    }
    if (creditBookmarks) {
      setCreditBookmarks(creditBookmarks);
    }
  }, [
    creditBookmarks,
    searchTerm,
    selectedInstitution,
    setCreditBookmarks,
    setSearchTerm,
    setSelectedSourceInstitution,
  ]);
  return children;
};

export const monsidoPages: { path: string; component: ReactNode; name: string }[] = [
  {
    path: '/home',
    name: 'defaultHome',
    component: (
      <MonsidoWrapperComponent>
        <Home />
      </MonsidoWrapperComponent>
    ),
  },
  {
    path: '/home/empty',
    name: 'withNoSearchTerm',
    component: (
      <MonsidoWrapperComponent selectedInstitution={''} searchTerm={''} creditBookmarks={[]}>
        <Home />
      </MonsidoWrapperComponent>
    ),
  },
  {
    // add ?tab=PRECEDENT or ?tab=ARTICULATION to the sitemap
    path: '/home/withPrecedent',
    name: 'withSearchTerm',
    component: (
      <MonsidoWrapperComponent selectedInstitution={'UAC'} searchTerm={'class'} creditBookmarks={[]}>
        <Home />
      </MonsidoWrapperComponent>
    ),
  },
  {
    path: '/home/withPathways',
    name: 'withPathway',
    component: (
      <MonsidoWrapperComponent selectedInstitution='UNI1' searchTerm='' creditBookmarks={[]}>
        <Home />
      </MonsidoWrapperComponent>
    ),
  },
  {
    path: '/details/articulations/:creditItemID',
    name: CreditType.ARTICULATION,
    component: <CreditDetails />,
  },
  {
    path: '/details/precedents/:creditItemID',
    name: CreditType.PRECEDENT,
    component: <CreditDetails />,
  },
  {
    path: '/saved-credits/empty',
    name: 'savedCreditsEmpty',
    component: (
      <MonsidoWrapperComponent selectedInstitution='UNI1' searchTerm='' creditBookmarks={[]}>
        <SavedCredits />
      </MonsidoWrapperComponent>
    ),
  },
  // use ?grouping=course or ?grouping=institution to toggle between the states
  {
    path: '/saved-credits',
    name: 'savedCredits',
    component: (
      <MonsidoWrapperComponent>
        <SavedCredits />
      </MonsidoWrapperComponent>
    ),
  },
  {
    path: '/saved-credits/withSavedCredits',
    name: 'withSavedCredits',
    component: (
      <MonsidoWrapperComponent selectedInstitution='UNI1' searchTerm='' creditBookmarks={mockCreditBookmarks}>
        <SavedCredits />
      </MonsidoWrapperComponent>
    ),
  },
  {
    path: 'help',
    name: 'help',
    component: <Help />,
  },
  {
    path: '404',
    name: '404',
    component: <Error404 />,
  },
];
